import { clsx, type ClassValue } from 'clsx';
import dayjs from 'dayjs';
import { API_BASE_URL, DATE_FORMAT, DATETIME_FORMAT, PLAN_NAMES } from '@/constants';

/**
 * Merge class names with clsx
 */
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

export function formatDateTime(value: unknown, type: 'date' | 'dateTime'): string {
  if (!value) return '';
  let dateValue: string | Date = '';
  if (typeof value === 'string' || value instanceof Date) {
    dateValue = value;
  } else if (typeof value === 'number') {
    dateValue = new Date(value);
  } else {
    return '';
  }
  const d = dayjs(dateValue);
  if (!d.isValid()) return '';
  if (type === 'date') {
    return d.format(DATE_FORMAT);
  }
  return d.format(DATETIME_FORMAT);
}
/**
 * Truncate string
 */
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

/**
 * Generate random ID
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Check if value is empty
 */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Sleep function for async operations
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Safe JSON parse
 */
export function safeJsonParse<T>(str: string, fallback: T): T {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
}

/**
 * Get initials from name
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Format number with commas
 */
export function formatNumber(num: number): string {
  return num.toLocaleString();
}

/**
 * Format bytes to human readable
 */
export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export const formatMessage = (template: string, ...params: Array<string | number>) => {
  return template.replace(/\{(\d+)}/g, (_, index: string) => {
    const value = params[Number(index)];
    return value !== undefined && value !== null ? String(value) : '';
  });
};

/**
 * Get plan name from plan code
 */
export function getPlanName(planCode: string): string {
  return PLAN_NAMES[planCode] || planCode;
}
// Get file URL
export function getFileUrl(imageUrl?: string): string {
  if (!imageUrl) return '';
  return `${API_BASE_URL}/files/${imageUrl}`;
}

